import {
  BoxGeometry,
  Color,
  CylinderGeometry,
  DoubleSide,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Object3D,
  RingGeometry,
  SphereGeometry,
  TorusGeometry,
  type ColorRepresentation,
} from 'three'
import {
  ADDITION,
  Brush,
  Evaluator,
  INTERSECTION,
  SUBTRACTION,
} from 'three-bvh-csg'
import { STLLoader } from 'three/examples/jsm/Addons.js'
import maxiprinter from '../../assets/models/maxiprinter.stl'
import { getColorFromTheme } from '../../helpers/theme'
import { MaxiPrinterIcon, AddMaxiPrinterIcon } from '../../components/icons'
import BasePrinterDriver from './base-printer-driver'
import { ThreeItemKeys, ThreeItemTypes } from '../../constants/three'
import { materialColors } from '../../constants/theme'

class MaxiPrinterDriver extends BasePrinterDriver {
  static readonly addIcon = AddMaxiPrinterIcon
  static readonly defaultGrid = 'polar' as const
  static readonly defaultLayerHeightMm = 20
  static readonly gcodeExtension = 'mxp'
  static readonly hasCartesianGrid = true
  static readonly hasFullPrintableZone = true
  static readonly hasGhostPlacement = false
  static readonly hasHalfPrintableZone = true
  static readonly hasPolarGrid = true
  static readonly icon = MaxiPrinterIcon
  static readonly model = 'maxiprinter'
  static readonly name = 'MaxiPrinter'
  static readonly printerOptions = {}

  private static readonly BASE_OFFSET_X_MM = 490
  private static readonly BASE_OFFSET_Y_MM = 120
  private static readonly BOOM_ANGLE_DEGREES = 77.5
  private static readonly DEFAULT_BOOM_LENGTH_MM = 5000
  private static readonly DEFAULT_HEIGHT_MM = 2000
  private static readonly DEFAULT_JIB_LENGTH_MM = 3100
  private static readonly GEOMETRY_SEGMENTS = 64
  private static readonly NOZZLE_LENGTH_MM = 650
  private static readonly SAFETY_OFFSET_MM = 200

  static async getPolarZone(gridColor: string): Promise<Object3D> {
    const boomMm = this.DEFAULT_BOOM_LENGTH_MM
    const jibMm = this.DEFAULT_JIB_LENGTH_MM
    const heightMm = this.DEFAULT_HEIGHT_MM
    const boomM = boomMm / 1000
    const jibM = jibMm / 1000
    const heightM = heightMm / 1000

    const polarGridGroup = new Group()
    polarGridGroup.name = 'Polar grid'
    polarGridGroup.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.POLAR_ZONE

    const minRadiusM = this.getMinRadiusM(boomM, jibM)
    const maxRadiusM = this.getMaxPrintableRadiusM(heightM, boomM, jibM)

    for (let radiusM = 0; radiusM <= maxRadiusM; radiusM += 1) {
      if (radiusM > minRadiusM) {
        polarGridGroup.add(this.getRingMesh(radiusM, 'white'))
      }
    }

    polarGridGroup.add(
      ...this.getMaxiprinterBaseZone(heightMm, boomMm, jibMm, gridColor),
    )

    return polarGridGroup
  };

  static async getCartesianZone(gridColor: string): Promise<Object3D> {
    const boomLengthMm = this.DEFAULT_BOOM_LENGTH_MM
    const jibLengthMm = this.DEFAULT_JIB_LENGTH_MM
    const heightMm = this.DEFAULT_HEIGHT_MM
    const boomLengthM = boomLengthMm / 1000
    const jibLengthM = jibLengthMm / 1000
    const heightM = heightMm / 1000

    const cubeCartesianGroup = new Group()
    cubeCartesianGroup.name = 'Cartesian Zone'
    cubeCartesianGroup.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.CARTESIAN_ZONE

    const maxPrintableRadiusM = this.getMaxPrintableRadiusM(heightM, boomLengthM, jibLengthM)
    const minRadiusM = this.getMinRadiusM(boomLengthM, jibLengthM)

    const boxMaterial = new MeshBasicMaterial({ color: 'white', side: DoubleSide })

    for (let offset = 0; offset <= maxPrintableRadiusM; offset += 1) {
      if (Math.abs(offset) <= maxPrintableRadiusM) {
        const length = Math.sqrt(maxPrintableRadiusM ** 2 - offset ** 2) * 2

        // For lines passing through the center (offset close to 0),
        // split them into segments that avoid the minRadius zone
        if (Math.abs(offset) < minRadiusM) {
          // Calculate the intersection points where the line crosses the minRadius circle
          const halfChordLength = Math.sqrt(minRadiusM ** 2 - offset ** 2)

          // Create two segments: one on each side of the minRadius zone
          const segment1Length = (length / 2) - halfChordLength
          const segment2Length = segment1Length

          if (segment1Length > 0.1) { // Only create segments if they're long enough to be visible
            // Z-axis lines (parallel to Z)
            // Segment 1: from -maxLength/2 to -halfChordLength
            const zNegativeSegment1Geometry = new BoxGeometry(segment1Length, 0.01, 0.02)
            const zNegativeSegment1Mesh = new Mesh(zNegativeSegment1Geometry, boxMaterial)
            zNegativeSegment1Mesh.position.set(-(halfChordLength + segment1Length / 2), 0, offset)

            // Segment 2: from halfChordLength to maxLength/2
            const zPositiveSegment1Geometry = new BoxGeometry(segment1Length, 0.01, 0.02)
            const zPositiveSegment1Mesh = new Mesh(zPositiveSegment1Geometry, boxMaterial)
            zPositiveSegment1Mesh.position.set(halfChordLength + segment1Length / 2, 0, offset)

            cubeCartesianGroup.add(zNegativeSegment1Mesh, zPositiveSegment1Mesh)

            if (offset !== 0) {
              const zNegativeSegment2Geometry = new BoxGeometry(segment2Length, 0.01, 0.02)
              const zNegativeSegment2Mesh = new Mesh(zNegativeSegment2Geometry, boxMaterial)
              zNegativeSegment2Mesh.position.set(-(halfChordLength + segment2Length / 2), 0, -offset)

              const zPositiveSegment2Geometry = new BoxGeometry(segment2Length, 0.01, 0.02)
              const zPositiveSegment2Mesh = new Mesh(zPositiveSegment2Geometry, boxMaterial)
              zPositiveSegment2Mesh.position.set(halfChordLength + segment2Length / 2, 0, -offset)

              cubeCartesianGroup.add(zNegativeSegment2Mesh, zPositiveSegment2Mesh)
            }

            // X-axis lines (parallel to X)
            // Segment 1: from -maxLength/2 to -halfChordLength
            const xNegativeSegment1Geometry = new BoxGeometry(0.02, 0.01, segment1Length)
            const xNegativeSegment1Mesh = new Mesh(xNegativeSegment1Geometry, boxMaterial)
            xNegativeSegment1Mesh.position.set(offset, 0, -(halfChordLength + segment1Length / 2))

            // Segment 2: from halfChordLength to maxLength/2
            const xPositiveSegment1Geometry = new BoxGeometry(0.02, 0.01, segment1Length)
            const xPositiveSegment1Mesh = new Mesh(xPositiveSegment1Geometry, boxMaterial)
            xPositiveSegment1Mesh.position.set(offset, 0, halfChordLength + segment1Length / 2)

            cubeCartesianGroup.add(xNegativeSegment1Mesh, xPositiveSegment1Mesh)

            if (offset !== 0) {
              const xNegativeSegment2Geometry = new BoxGeometry(0.02, 0.01, segment2Length)
              const xNegativeSegment2Mesh = new Mesh(xNegativeSegment2Geometry, boxMaterial)
              xNegativeSegment2Mesh.position.set(-offset, 0, -(halfChordLength + segment2Length / 2))

              const xPositiveSegment2Geometry = new BoxGeometry(0.02, 0.01, segment2Length)
              const xPositiveSegment2Mesh = new Mesh(xPositiveSegment2Geometry, boxMaterial)
              xPositiveSegment2Mesh.position.set(-offset, 0, halfChordLength + segment2Length / 2)

              cubeCartesianGroup.add(xNegativeSegment2Mesh, xPositiveSegment2Mesh)
            }
          }
        }
        else {
          // Lines outside the minRadius zone - draw them normally (full length)
          const zPositiveBoxGeometry = new BoxGeometry(length, 0.01, 0.02)
          const zPositiveBoxMesh = new Mesh(zPositiveBoxGeometry, boxMaterial)
          zPositiveBoxMesh.position.z = offset

          const zNegativeBoxGeometry = new BoxGeometry(length, 0.01, 0.02)
          const zNegativeBoxMesh = new Mesh(zNegativeBoxGeometry, boxMaterial)
          zNegativeBoxMesh.position.z = -offset

          const xPositiveBoxGeometry = new BoxGeometry(0.02, 0.01, length)
          const xPositiveBoxMesh = new Mesh(xPositiveBoxGeometry, boxMaterial)
          xPositiveBoxMesh.position.x = offset

          const xNegativeBoxGeometry = new BoxGeometry(0.02, 0.01, length)
          const xNegativeBoxMesh = new Mesh(xNegativeBoxGeometry, boxMaterial)
          xNegativeBoxMesh.position.x = -offset

          cubeCartesianGroup.add(xPositiveBoxMesh, xNegativeBoxMesh, zPositiveBoxMesh, zNegativeBoxMesh)
        }
      }
    }

    cubeCartesianGroup.add(...this.getMaxiprinterBaseZone(heightMm, boomLengthMm, jibLengthMm, gridColor).map((mesh) => {
      mesh.rotateX(Math.PI / 2)
      return mesh
    }))

    cubeCartesianGroup.rotateX(Math.PI / 2)

    return cubeCartesianGroup
  };

  static async getPrintableZone(type: PrintableZoneType, color: string): Promise<Object3D> {
    const boomLengthM = this.DEFAULT_BOOM_LENGTH_MM / 1000
    const jibLengthM = this.DEFAULT_JIB_LENGTH_MM / 1000
    const heightM = this.DEFAULT_HEIGHT_MM / 1000

    const evaluator = new Evaluator()

    let finalZone: Brush
    const boomJibUnion = evaluator.evaluate(this.createJibBrush(boomLengthM, jibLengthM), this.createBoomBrush(boomLengthM, jibLengthM), ADDITION)
    const q = evaluator.evaluate(this.createOuterSphereBrush(boomLengthM, jibLengthM), boomJibUnion, SUBTRACTION)
    const s = evaluator.evaluate(q, this.createInnerSphereBrush(boomLengthM, jibLengthM), SUBTRACTION)
    const x = evaluator.evaluate(s, this.createInnerCylinder(boomLengthM, jibLengthM), SUBTRACTION)
    const w = evaluator.evaluate(x, this.createPrintableCylinder(heightM, boomLengthM, jibLengthM), INTERSECTION)

    if (type === 'half') {
      const d = evaluator.evaluate(w, this.createSliceCubeBrush(boomLengthM, jibLengthM), SUBTRACTION)
      finalZone = evaluator.evaluate(d, this.createGroundBrush(heightM, boomLengthM, jibLengthM), SUBTRACTION)
    }
    else {
      finalZone = evaluator.evaluate(w, this.createGroundBrush(heightM, boomLengthM, jibLengthM), SUBTRACTION)
    }

    finalZone.material = new MeshStandardMaterial({
      metalness: 0.4,
      roughness: 0.8,
      transparent: true,
      opacity: 0.4,
      color: new Color(getColorFromTheme(`--color-picker-${color}-solid`)),
      side: DoubleSide,
    })

    finalZone.position.z = heightM
    finalZone.rotation.x = Math.PI / 2

    return finalZone
  };

  static getDefaultUserData() {
    return {
      [ThreeItemKeys.PRINTER_BOOM]: this.DEFAULT_BOOM_LENGTH_MM,
      [ThreeItemKeys.PRINTER_HEIGHT]: this.DEFAULT_HEIGHT_MM,
      [ThreeItemKeys.PRINTER_JIB]: this.DEFAULT_JIB_LENGTH_MM,
    }
  }

  static getMesh(colorOffset: number, forcedColor?: string) {
    return new Promise<Object3D>((resolve, reject) => {
      const loader = new STLLoader()
      loader.load(maxiprinter, (geometry) => {
        const printerMesh = new Mesh(geometry, this.getPrinterMaterial(colorOffset, forcedColor))
        printerMesh.userData[ThreeItemKeys.ITEM_TYPE] = ThreeItemTypes.PRINTER_MESH
        resolve(printerMesh)
      }, () => { }, (error) => {
        reject(error)
      })
    })
  }

  private static getPrinterMaterial(offset: number, forcedColor?: string) {
    return new MeshStandardMaterial({
      color: getColorFromTheme(`--color-picker-${forcedColor ?? materialColors[offset % materialColors.length]}-solid`),
      side: DoubleSide,
      metalness: 0.3,
      roughness: 0.3,
    })
  }

  private static getGroundY(printerHeightMm: number) {
    return this.BASE_OFFSET_Y_MM - this.NOZZLE_LENGTH_MM + printerHeightMm
  }

  private static getMinRadiusM(boomLengthM: number, jibLengthM: number) {
    return (boomLengthM - jibLengthM) + (this.SAFETY_OFFSET_MM / 1000)
  }

  private static getMaxRadiusM(boomLengthM: number, jibLengthM: number) {
    return boomLengthM + jibLengthM - (this.SAFETY_OFFSET_MM / 1000) - (this.BASE_OFFSET_X_MM / 1000)
  }

  private static getDoubleMaxRadiusM(boomLengthM: number, jibLengthM: number) {
    return this.getMaxRadiusM(boomLengthM, jibLengthM) * 2
  }

  private static getMaxPrintableRadiusM(printerHeightM: number, boomLengthM: number, jibLengthM: number) {
    return Math.sqrt(
      this.getMaxRadiusM(boomLengthM, jibLengthM) * this.getMaxRadiusM(boomLengthM, jibLengthM) - printerHeightM * printerHeightM,
    )
  }

  private static getBoomHeightYM(boomLengthM: number) {
    return boomLengthM * Math.sin((Math.PI / 180) * this.BOOM_ANGLE_DEGREES)
  }

  private static getBoomDistanceXM(boomLengthM: number) {
    return boomLengthM * Math.cos((Math.PI / 180) * this.BOOM_ANGLE_DEGREES) - (this.BASE_OFFSET_X_MM / 1000)
  }

  private static getRingMesh(radius: number, color: ColorRepresentation, weight = 0.02, type: string | null = null) {
    const ringMaterial = new MeshBasicMaterial({ color, side: DoubleSide })
    const ringGeometry = new RingGeometry(radius, radius - weight, 128)
    const ringMesh = new Mesh(ringGeometry, ringMaterial)

    if (type) {
      ringMesh.userData[ThreeItemKeys.ITEM_TYPE] = type
    }

    return ringMesh
  }

  private static getMaxiprinterBaseZone(printerHeightMm: number, boomLengthMm: number, jibLengthMm: number, gridColor: string) {
    const minRadiusM = this.getMinRadiusM(boomLengthMm / 1000, jibLengthMm / 1000)
    const maxRadiusM = this.getMaxPrintableRadiusM(printerHeightMm / 1000, boomLengthMm / 1000, jibLengthMm / 1000)

    return [
      this.getRingMesh(minRadiusM, gridColor, 0.05, ThreeItemTypes.INNER_ZONE_RING),
      this.getRingMesh(maxRadiusM, gridColor, 0.05, ThreeItemTypes.OUTER_ZONE_RING),
    ]
  }

  private static createGroundBrush(printerHeightM: number, boomLengthM: number, jibLengthM: number) {
    const doubleMaxRadius = this.getDoubleMaxRadiusM(boomLengthM, jibLengthM)
    const brush = new Brush(
      new BoxGeometry(
        doubleMaxRadius,
        doubleMaxRadius,
        doubleMaxRadius,
      ),
    )
    brush.position.y = -this.getMaxRadiusM(boomLengthM, jibLengthM) - (printerHeightM)
    brush.updateMatrixWorld()
    return brush
  }

  private static createSliceCubeBrush(boomLengthM: number, jibLengthM: number) {
    const doubleMaxRadiusM = this.getDoubleMaxRadiusM(boomLengthM, jibLengthM)
    const brush = new Brush(
      new BoxGeometry(
        doubleMaxRadiusM,
        doubleMaxRadiusM,
        doubleMaxRadiusM,
      ),
    )
    brush.position.z = this.getMaxRadiusM(boomLengthM, jibLengthM)
    brush.updateMatrixWorld()
    return brush
  }

  private static createBoomBrush(boomLengthM: number, jibLengthM: number) {
    const minRadiusM = this.getMinRadiusM(boomLengthM, jibLengthM)
    const brush = new Brush(
      new CylinderGeometry(
        minRadiusM,
        minRadiusM,
        this.GEOMETRY_SEGMENTS,
        this.GEOMETRY_SEGMENTS,
      ),
    )
    brush.updateMatrixWorld()
    return brush
  }

  private static createJibBrush(boomLengthM: number, jibLengthM: number) {
    const brush = new Brush(
      new TorusGeometry(
        this.getBoomDistanceXM(boomLengthM),
        jibLengthM + (this.SAFETY_OFFSET_MM / 1000),
        this.GEOMETRY_SEGMENTS,
        this.GEOMETRY_SEGMENTS,
      ),
    )
    brush.position.y = this.getBoomHeightYM(boomLengthM)
    brush.rotation.x = Math.PI / 2
    brush.updateMatrixWorld()
    return brush
  }

  private static createInnerSphereBrush(boomLengthM: number, jibLengthM: number) {
    const brush = new Brush(
      new SphereGeometry(
        jibLengthM + (this.SAFETY_OFFSET_MM / 2000),
        this.GEOMETRY_SEGMENTS,
        this.GEOMETRY_SEGMENTS,
      ),
    )
    brush.position.y = this.getBoomHeightYM(boomLengthM)
    brush.updateMatrixWorld()
    return brush
  }

  private static createInnerCylinder(boomLengthM: number, jibLengthM: number) {
    const radius = this.getBoomDistanceXM(boomLengthM) + jibLengthM + (this.SAFETY_OFFSET_MM / 1000)
    const brush = new Brush(
      new CylinderGeometry(radius, radius, radius),
    )
    brush.position.y = this.getBoomHeightYM(boomLengthM) + radius / 2
    brush.updateMatrixWorld()
    return brush
  }

  private static createOuterSphereBrush(boomLengthM: number, jibLengthM: number) {
    const brush = new Brush(
      new SphereGeometry(
        this.getMaxRadiusM(boomLengthM, jibLengthM),
        this.GEOMETRY_SEGMENTS,
        this.GEOMETRY_SEGMENTS,
      ),
    )
    brush.updateMatrixWorld()
    return brush
  }

  private static createPrintableCylinder(printerHeightM: number, boomLengthM: number, jibLengthM: number) {
    const brush = new Brush(
      new CylinderGeometry(
        this.getMaxPrintableRadiusM(printerHeightM, boomLengthM, jibLengthM),
        this.getMaxPrintableRadiusM(printerHeightM, boomLengthM, jibLengthM),
        this.GEOMETRY_SEGMENTS,
        this.GEOMETRY_SEGMENTS,
      ),
    )
    brush.updateMatrixWorld()
    return brush
  }
}

export default MaxiPrinterDriver
